import { useCallback, useMemo } from "react";
import Loading from "../loading";
import Container from "../container";

import Timeline, {
  TimelineHeaders,
  SidebarHeader,
  DateHeader,
  TimelineMarkers,
  TodayMarker
} from "react-calendar-timeline";

import moment from "moment";
import { toTimeline } from "./schedule";

import "./Timeline.scss";
import "./style.scss";

const groupRenderer = ({ group }) => {
  return (
    <div className={`scheduler-group-${group.type}`}>
      <span className="title">{group.title}</span>
    </div>
  );
};

const itemRenderer = ({
  item,
  timelineContext,
  itemContext,
  getItemProps,
  getResizeProps
}) => {
  const { left: leftResizeProps, right: rightResizeProps } = getResizeProps();

  return (
    <div
      {...getItemProps({
        className: `scheduled-item  scheduled-${item.colorGroup} ${
          itemContext.selected ? "selected" : ""
        }`,
        onMouseDown: () => {
          //console.log("on item click", item);
        }
      })}
      key={item.id}
    >
      {itemContext.useResizeHandle ? <div {...leftResizeProps} /> : null}

      <div
        style={{
          height: itemContext.dimensions.height
        }}
      >
        {itemContext.title}
      </div>

      {itemContext.useResizeHandle ? <div {...rightResizeProps} /> : null}
    </div>
  );
};

const asList = (rows) => (Array.isArray(rows) ? rows : rows ? [rows] : []);

// The timeline of scheduled rules or blocks (rows: a row or an array). It
// writes nothing: a drag is handed to onChange(rowId, change), change
// { scheduleId, subId, time, edge }, which applies it (applyScheduleChange)
// where the rows live: the values of the open form (./form) or the draft of
// the scheduling page (./page). Without onChange the timeline is read-only.
// onOpen(rowId): double click on a range.
export default function ScheduleDiagram({ itemType, rows, onChange, onOpen }) {
  const { groups, items } = useMemo(
    () => toTimeline(asList(rows), itemType),
    [itemType, rows]
  );
  const itemsById = useMemo(
    () => new Map(items.map((item) => [item.id, item])),
    [items]
  );

  const onResize = useCallback(
    (itemId, time, edge) => {
      const item = itemsById.get(itemId);
      if (!item || !onChange) return;

      onChange(item.group, {
        scheduleId: item.id,
        subId: item.subId,
        time,
        edge
      });
    },
    [itemsById, onChange]
  );

  const onMove = useCallback(
    (itemId, time) => onResize(itemId, time, "move"),
    [onResize]
  );

  const onItemClick = useCallback(
    (itemId) => {
      const item = itemsById.get(itemId);
      if (item && onOpen) onOpen(item.group);
    },
    [itemsById, onOpen]
  );

  if (items.length === 0)
    return (
      <Container title="Scheduling" foldable={false}>
        <Loading $message="NO SCHEDULING" $height="500px" $width="100%" />
      </Container>
    );

  return (
    <div>
      <div className="diagram-container">
        <Timeline
          groups={groups}
          items={items}
          stackItems={true}
          canChangeGroup={false}
          defaultTimeStart={moment().add(-1, "month")}
          defaultTimeEnd={moment().add(1, "month")}
          showCursorLine
          canMove={Boolean(onChange)}
          canResize={Boolean(onChange)}
          groupRenderer={groupRenderer}
          itemRenderer={itemRenderer}
          itemHeightRatio={0.8}
          onItemResize={onResize}
          onItemMove={onMove}
          onItemDoubleClick={onItemClick}
        >
          <TimelineMarkers>
            <TodayMarker interval={2000}>
              {/* custom renderer for this marker */}
              {({ styles, date }) => {
                const customStyles = {
                  ...styles,
                  backgroundColor: "deeppink",
                  width: "2px"
                };
                return (
                  <div
                    style={customStyles}
                    onClick={() => console.log("CLICK")}
                  />
                );
              }}
            </TodayMarker>
          </TimelineMarkers>
          <TimelineHeaders className="sticky">
            <SidebarHeader>
              {({ getRootProps }) => {
                return <div {...getRootProps()}></div>;
              }}
            </SidebarHeader>
            <DateHeader unit="primaryHeader" labelFormat="MM/DD" />
            <DateHeader />
          </TimelineHeaders>
        </Timeline>
      </div>
    </div>
  );
}
